import { useState } from 'react'
import { Check, Clipboard, Code2, KeyRound, ShieldCheck } from 'lucide-react'

import type { Locale } from '../i18n'
import curlExample from '../../docs/examples/curl.sh?raw'
import javaExample from '../../docs/examples/TopCaptchaExample.java?raw'
import nodeExample from '../../docs/examples/node.mjs?raw'
import phpExample from '../../docs/examples/php.php?raw'
import pythonExample from '../../docs/examples/python.py?raw'

type Language = 'cURL' | 'Node.js' | 'Python' | 'PHP' | 'Java'

const createRequest = `{
  "clientKey": "tc_live_REPLACE_ME",
  "task": {
    "type": "HCaptchaTaskProxyless",
    "websiteURL": "https://example.com/login",
    "websiteKey": "site-key",
    "isInvisible": false,
    "enterprisePayload": { "rqdata": "optional" }
  }
}`

const processingResponse = `{
  "errorId": 0,
  "taskId": "tc_task_...",
  "status": "processing"
}`

const readyResponse = `{
  "errorId": 0,
  "taskId": "tc_task_...",
  "status": "ready",
  "solution": {
    "gRecaptchaResponse": "token",
    "userAgent": "Chrome/154"
  },
  "cost": "0.002400",
  "currency": "USD",
  "createTime": 1790842000,
  "endTime": 1790842008,
  "solveCount": 1
}`

const examples: Record<Language, string> = {
  cURL: curlExample.trim(),
  'Node.js': nodeExample.trim(),
  Python: pythonExample.trim(),
  PHP: phpExample.trim(),
  Java: javaExample.trim()
}

const errors: Record<Locale, string[][]> = {
  en: [
    ['ERROR_KEY_DOES_NOT_EXIST', 'API key is invalid, revoked, or lacks access.'],
    ['ERROR_ZERO_BALANCE', 'Available USD balance cannot fund the task.'],
    ['ERROR_TASK_ABSENT', 'The task payload or one of its fields is invalid.'],
    ['ERROR_TASK_NOT_SUPPORTED', 'The requested task type is not supported.'],
    ['ERROR_TASK_NOT_FOUND', 'The task does not exist for this customer.'],
    ['ERROR_TASK_LIMIT_REACHED', 'The customer concurrency limit was reached.'],
    ['ERROR_IDEMPOTENCY_CONFLICT', 'The key was reused with a different task.'],
    ['ERROR_CAPTCHA_UNSOLVABLE', 'The task reached a terminal solve failure.'],
    ['RESULT_EXPIRED', 'The result retention window has ended.']
  ],
  'zh-CN': [
    ['ERROR_KEY_DOES_NOT_EXIST', 'API 密钥无效、已撤销或没有所需权限。'],
    ['ERROR_ZERO_BALANCE', '可用美元余额不足以创建任务。'],
    ['ERROR_TASK_ABSENT', '任务内容或其中一个字段无效。'],
    ['ERROR_TASK_NOT_SUPPORTED', '不支持请求的任务类型。'],
    ['ERROR_TASK_NOT_FOUND', '该客户名下不存在此任务。'],
    ['ERROR_TASK_LIMIT_REACHED', '已达到客户并发任务上限。'],
    ['ERROR_IDEMPOTENCY_CONFLICT', '同一幂等键已用于其他任务内容。'],
    ['ERROR_CAPTCHA_UNSOLVABLE', '任务已终止且无法完成解答。'],
    ['RESULT_EXPIRED', '任务结果已超过保留期限。']
  ]
}

function Endpoint({ path, title, description }: { path: string; title: string; description: string }) {
  return <header className="docs-endpoint"><div><span>POST</span><code>{path}</code></div><h2>{title}</h2><p>{description}</p></header>
}

function JsonBlock({ label, value }: { label: string; value: string }) {
  return <div className="docs-code"><div className="docs-code-head"><span>{label}</span></div><pre><code>{value}</code></pre></div>
}

export function ApiDocs({ locale }: { locale: Locale }) {
  const zh = locale === 'zh-CN'
  const [language, setLanguage] = useState<Language>('cURL')
  const [copied, setCopied] = useState(false)
  const copyExample = async () => {
    await navigator.clipboard.writeText(examples[language])
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  const labels = zh ? {
    auth: '身份认证', create: '创建任务', result: '获取任务结果', balance: '查询余额', fields: '任务字段', errors: '错误代码', examples: '代码示例', rest: '原生 REST API'
  } : {
    auth: 'Authentication', create: 'Create a task', result: 'Get task result', balance: 'Get balance', fields: 'Task fields', errors: 'Error codes', examples: 'Code examples', rest: 'Native REST API'
  }
  return <div className="developer-center">
    <header className="docs-hero">
      <div><span className="eyebrow">API VERSION 1.1</span><h1>Proxyless hCaptcha API</h1><p>{zh ? '一套稳定、通用的 JSON 任务接口，适用于服务端集成。' : 'A stable, portable JSON task API for server-side integrations.'}</p></div>
      <div className="docs-base"><small>BASE URL</small><code>https://captcha.topgold.duckdns.org</code></div>
    </header>
    <div className="docs-layout">
      <nav className="docs-toc" aria-label={zh ? '文档目录' : 'Documentation'}>
        <strong>{zh ? '开始使用' : 'Get started'}</strong>
        {[['authentication', labels.auth], ['create', labels.create], ['result', labels.result], ['balance', labels.balance], ['fields', labels.fields], ['errors', labels.errors], ['examples', labels.examples], ['rest', labels.rest]].map(([id, label]) => <a href={`#${id}`} key={id}>{label}</a>)}
      </nav>
      <article className="docs-article">
        <section id="authentication">
          <div className="docs-section-icon"><KeyRound /></div><h2>{labels.auth}</h2>
          <p>{zh ? '兼容接口把 API Key 放在 JSON 的 clientKey 字段中。密钥只能保存在服务端，不能写入浏览器、移动端或公开仓库。' : 'Compatibility endpoints receive the API key in the JSON clientKey field. Keep live keys on your server, never in browser code, mobile apps, or public repositories.'}</p>
          <div className="docs-notice"><ShieldCheck /><span>{zh ? '生产密钥按权限范围隔离；测试与生产使用不同密钥。' : 'Keys are scope-limited. Use separate credentials for test and production.'}</span></div>
        </section>
        <section id="create">
          <Endpoint path="/createTask" title={labels.create} description={zh ? '提交一个 Proxyless hCaptcha 任务，成功后返回稳定的 taskId。' : 'Submit one proxyless hCaptcha task and receive a stable taskId.'} />
          <JsonBlock label={zh ? '请求' : 'Request'} value={createRequest} />
          <JsonBlock label={zh ? '成功响应' : 'Success response'} value={'{\n  "errorId": 0,\n  "taskId": "tc_task_..."\n}'} />
        </section>
        <section id="result">
          <Endpoint path="/getTaskResult" title={labels.result} description={zh ? '每两秒轮询一次，直到状态从 processing 变为 ready。' : 'Poll every two seconds until status changes from processing to ready.'} />
          <div className="docs-response-grid"><JsonBlock label="PROCESSING" value={processingResponse} /><JsonBlock label="READY" value={readyResponse} /></div>
        </section>
        <section id="balance">
          <Endpoint path="/getBalance" title={labels.balance} description={zh ? '返回可用美元余额，精确到六位小数。' : 'Return available USD balance with six decimal places.'} />
          <JsonBlock label={zh ? '响应' : 'Response'} value={'{\n  "errorId": 0,\n  "balance": "12.447600",\n  "currency": "USD"\n}'} />
        </section>
        <section id="fields">
          <h2>{labels.fields}</h2>
          <div className="docs-table-wrap"><table className="docs-table"><thead><tr><th>{zh ? '字段' : 'Field'}</th><th>{zh ? '类型' : 'Type'}</th><th>{zh ? '必填' : 'Required'}</th><th>{zh ? '说明' : 'Description'}</th></tr></thead><tbody>
            <tr><td><code>type</code></td><td>string</td><td>{zh ? '是' : 'Yes'}</td><td><code>HCaptchaTaskProxyless</code></td></tr>
            <tr><td><code>websiteURL</code></td><td>HTTPS URL</td><td>{zh ? '是' : 'Yes'}</td><td>{zh ? '包含验证码的公开页面' : 'Public page containing the widget'}</td></tr>
            <tr><td><code>websiteKey</code></td><td>string</td><td>{zh ? '是' : 'Yes'}</td><td>hCaptcha sitekey</td></tr>
            <tr><td><code>isInvisible</code></td><td>boolean</td><td>{zh ? '否' : 'No'}</td><td>{zh ? '默认 false' : 'Defaults to false'}</td></tr>
            <tr><td><code>enterprisePayload.rqdata</code></td><td>string</td><td>{zh ? '否' : 'No'}</td><td>{zh ? 'Enterprise challenge 数据' : 'Enterprise challenge data'}</td></tr>
            <tr><td><code>locale</code></td><td>string</td><td>{zh ? '否' : 'No'}</td><td><code>en</code>, <code>zh-CN</code></td></tr>
          </tbody></table></div>
          <p className="docs-caveat">{zh ? 'Proxyless 任务不接受 proxy、cookies 或 callbackUrl。未知字段会返回 ERROR_TASK_ABSENT，不会被静默忽略。' : 'Proxyless tasks do not accept proxy, cookies, or callbackUrl. Unknown fields return ERROR_TASK_ABSENT instead of being silently ignored.'}</p>
        </section>
        <section id="errors">
          <h2>{labels.errors}</h2>
          <div className="docs-table-wrap"><table className="docs-table"><thead><tr><th>Code</th><th>{zh ? '含义' : 'Meaning'}</th></tr></thead><tbody>{errors[locale].map(([code, meaning]) => <tr key={code}><td><code>{code}</code></td><td>{meaning}</td></tr>)}</tbody></table></div>
        </section>
        <section id="examples">
          <h2>{labels.examples}</h2>
          <div className="code-tabs" role="tablist" aria-label={zh ? '代码语言' : 'Code language'}>{(Object.keys(examples) as Language[]).map(item => <button type="button" role="tab" aria-selected={language === item} className={language === item ? 'active' : ''} onClick={() => { setLanguage(item); setCopied(false) }} key={item}>{item}</button>)}</div>
          <div className="docs-code docs-example" role="tabpanel"><div className="docs-code-head"><span>{language}</span><button type="button" className="icon-button" aria-label={`${copied ? 'Copied' : 'Copy'} ${language} example`} title={zh ? '复制示例' : 'Copy example'} onClick={copyExample}>{copied ? <Check /> : <Clipboard />}</button></div><pre><code>{examples[language]}</code></pre></div>
        </section>
        <section id="rest">
          <div className="docs-section-icon"><Code2 /></div><h2>{labels.rest}</h2>
          <p>{zh ? '需要标准 HTTP 状态码、Bearer 认证和任务列表时，使用原生 REST API。' : 'Use the native REST API when you need HTTP status semantics, Bearer authentication, and task listing.'}</p>
          <div className="docs-rest-list"><div><span>POST</span><code>/api/v1/tasks</code></div><div><span>GET</span><code>/api/v1/tasks/:taskId</code></div><div><span>GET</span><code>/api/v1/tasks</code></div></div>
        </section>
      </article>
    </div>
  </div>
}
